import useUserVarStore from "@app/store/useUserVarStore";
import { GalleryCarouselTypes } from "@app/types/Opensearch.d";
import ArticleStickyBar from "@components/Blocks/ArticleStickyBar/ArticleStickyBar";
import AuthorCard from "@components/Blocks/Card/Author/AuthorCard";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import { SocialVariant } from "@components/Blocks/ShareSocial/ShareSocial";
import ShareSocialNew from "@components/Blocks/ShareSocial/ShareSocialNew";
import ArticleCarousel from "@components/Layouts/Carousel/ArticleCarousel";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import BeyondWordsPlayer from "@elements/BeyondWordsPlayer/BeyondWordsPlayer";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import Link from "@elements/Typography/Link";
import { StandFirst } from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import {
  findStyleBySectionAndSubCategory,
  isArrayWithElements,
} from "@helper/utils";
import { useArticleReactions } from "@hooks/useArticleReactions";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import classNames from "classnames";
import React from "react";

import ArticleElementWrapper from "../Components/ArticleElementWrapper";
import ContentAccessLabel from "../Components/ContentAccessLabel";

export type ArticleContentCardNewProps = {
  mainArticle: ProcessedArticleData;
  pageNumber: number;
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  index?: number;
  visibleArticleId?: string;
};

export default function ArticleContentCardNew({
  mainArticle,
  pageNumber,
  isInfiniteScrollArticle = false,
  gsChannels,
  articleSlotTarget,
  index,
  visibleArticleId,
}: ArticleContentCardNewProps) {
  const { userToken } = useUserVarStore();
  const {
    sectionName,
    uniqueSectionName,
    title,
    tags,
    authors,
    date,
    datetime,
    time,
    articleBody,
    standFirst,
    contentAccess,
    urlPath,
    imageGallery,
    relatedArticles,
    attachments,
    id,
    sourceId,
    linkToSection,
    adsUniqueSectionName,
    campaignReference,
  } = mainArticle;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName,
  });

  const socialIconList = ["facebook", "telegram", "plainWhatsapp", "newsPost"];
  const pageUrl = `${urlPath}`;
  const renderItems = imageGallery.filter(
    (item) => item.type !== GalleryCarouselTypes.UNKNOWN,
  );
  const sectionColor = mainArticle.parentSection
    ? findStyleBySectionAndSubCategory(mainArticle.parentSection)
        ?.selectedColor || ""
    : findStyleBySectionAndSubCategory(sectionName || "")?.selectedColor || "";
  const showTTSPlayer = shouldShowTTSPlayer(datetime, contentAccess);
  const isCampaign = Array.isArray(campaignReference)
    ? campaignReference.length > 0
    : !!(campaignReference?.href && campaignReference.title);
  const elementLink = isCampaign
    ? `mata-mata/${campaignReference?.href.split("/").at(-1)}`
    : linkToSection;

  const sectionTitleProps = {
    Element: Link,
    className:
      "text-[16px] lg:text-[12px] lg:font-semibold mb-0 leading-[120%]" +
      (isCampaign ? " text-blue-300 hover:text-blue-700 hover:underline" : ""),
    sectionName: isCampaign ? campaignReference?.title : sectionName,
    parentSection: isCampaign ? sectionName : mainArticle.parentSection,
    iconStyle: "hidden" as const,
  };

  const [isStickyVisible, setIsStickyVisible] = React.useState(false);

  return (
    <div
      key={index}
      id={`${id}-article-body`}
      className={classNames({ v_showStickyBar: isStickyVisible })}
    >
      <ArticleStickyBar
        article={mainArticle}
        pageNumber={pageNumber}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        onVisibilityChange={setIsStickyVisible}
        isActive={visibleArticleId === id}
      />
      <ArticleElementWrapper>
        <div
          className={`flex items-center pb-3 mb-3 border-b-2 border-${sectionColor}`}
          data-testid="section-share-wrapper"
        >
          <SectionTitleV2 {...sectionTitleProps} elementLink={elementLink} />
        </div>
        <div className="flex flex-col gap-[5px] mb-3">
          <ContentAccessLabel contentAccess={contentAccess} showIcon={false} />
          <Heading
            Element="h1"
            className="font-secondary leading-[120%] text-black-800 text-[22px] lg:text-[38px] font-medium"
            sourceId={sourceId}
          >
            {title}
          </Heading>
          {standFirst && (
            <StandFirst className="text-xs lg:text-xs font-primary font-[500] leading-[150%] text-grey-350">
              {standFirst}
            </StandFirst>
          )}
        </div>
        {/* Tags dont dont render If alias not available in payload*/}
        {!showTTSPlayer && (
          <RenderTags tagsWrapper="my-3 gap-[10px]" tags={tags} />
        )}
        <div className="flex mb-3">
          <Date
            date={date}
            time={time}
            variant={"or"}
            className="text-[14px] font-semimedium leading-[150%] text-black-700"
            dateTime={datetime}
          />
        </div>
        <div className="flex w-full justify-between mb-3">
          <div>
            {isArrayWithElements(authors) && (
              <div className="flex flex-row gap-3 flex-wrap">
                {authors.map(
                  (author, index) =>
                    author.hasProfile && (
                      <AuthorCard
                        key={index}
                        author={author}
                        displayHeight={44}
                        displayWidth={44}
                        imageWrapper="w-[46px] rounded-[55px] border border-white-600"
                        authorCardWrapper="gap-[5px]"
                        nameClass="text-[12px] lg:text-[12px] font-medium text-blue-400 leading-[150%]"
                        hasEmail={false}
                        forAuthorListing={false}
                      />
                    ),
                )}
              </div>
            )}
          </div>
          <ReactionToolbar
            articleId={id}
            showReactions={showReactions}
            reactions={reactions}
            userReactions={userReactions}
            reactionsError={reactionsError}
            handleReactionClick={handleReactionClick}
          />
        </div>
        <ArticleStickyBar
          article={mainArticle}
          pageNumber={pageNumber}
          isStatic={true}
          gsChannels={gsChannels}
          articleSlotTarget={articleSlotTarget}
        />
        {showTTSPlayer && (
          <BeyondWordsPlayer
            cueId={id}
            isInfiniteScrollArticle={isInfiniteScrollArticle}
          />
        )}
        {isArrayWithElements(renderItems) && (
          <ArticleCarousel
            renderItems={renderItems}
            sectionName={sectionName}
            wrapperClass="max-w-[860px]"
          />
        )}
      </ArticleElementWrapper>

      <ArticleParagraph
        freeArticle={mainArticle}
        articleId={id}
        contentAccess={contentAccess}
        articleBody={articleBody}
        uniqueSectionName={uniqueSectionName}
        relatedArticles={relatedArticles}
        pageNumber={pageNumber}
        isInfiniteScrollArticle={isInfiniteScrollArticle}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        visibleArticleId={visibleArticleId}
        adsUniqueSectionName={adsUniqueSectionName}
        attachments={attachments}
        tags={tags}
        showReactions
        reactions={reactions}
        handleReactionClick={handleReactionClick}
        userReactions={userReactions}
      />

      <ArticleElementWrapper>
        <div
          className="flex self-end items-center justify-end my-6 lg:my-8 track-impression"
          data-section-name="end-of-article"
        >
          {socialIconList.map((variant, index) => (
            <ShareSocialNew
              variant={variant as SocialVariant}
              pagePath={pageUrl}
              pageTitle={title}
              dateCreated={datetime}
              key={index}
            />
          ))}
        </div>
      </ArticleElementWrapper>
    </div>
  );
}
